import type { Profile } from "@fusioncraft/shared";
import { useEffect, useRef, useState } from "react";

export function ProfileSwitcher({
  profiles,
  active,
  onSwitch,
  onManage,
  onNew,
}: {
  profiles: Profile[];
  active: Profile;
  onSwitch: (id: string) => void;
  onManage: () => void;
  onNew: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  const pick = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };

  return (
    <div className="switcher" ref={ref}>
      <button
        className="switcher-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        title={active.model}
      >
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <rect x="2" y="2.5" width="12" height="11" rx="1.5" />
          <path d="M5 6h6M5 8.5h6M5 11h3" />
        </svg>
        {active.name}
      </button>
      {open && (
        <div className="menu" role="menu">
          {profiles.map((p) => (
            <button
              key={p.id}
              role="menuitemradio"
              aria-checked={p.id === active.id}
              className="menu-item"
              onClick={pick(() => onSwitch(p.id))}
            >
              <span className="menu-item-name">{p.name}</span>
              <span className="menu-item-meta">
                <code>{p.model}</code> · {p.elementCount}
                {p.scope === "private" && " · private"}
              </span>
            </button>
          ))}
          <hr />
          <button role="menuitem" className="menu-item" onClick={pick(onNew)}>
            New profile
          </button>
          <button role="menuitem" className="menu-item" onClick={pick(onManage)}>
            Manage profiles
          </button>
        </div>
      )}
    </div>
  );
}
